import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getQuotationEvaluation, saveEvaluation } from '../../models/quotationModel';
import { API_BASE_URL } from '../../config';
import { Eye, Download } from 'lucide-react';

// Utility function to clean and parse financial values
const parseFinancialValue = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  
  // Convert to string and clean it
  const cleanValue = value.toString()
    .replace(/\s/g, '')           // Remove all spaces
    .replace(/[^\d.-]/g, '')      // Remove all non-numeric characters except . and -
    .replace(/^-+/, '-')          // Keep only one leading minus sign
    .replace(/\.+/g, '.');        // Keep only one decimal point
  
  return parseFloat(cleanValue) || 0;
};

const QuoteViewModal = ({ isOpen, onClose, quote, quoteId, rfqId, requisitionTitle, canMakeCorrections, specificationId, requisitionId, onEvaluationSave }) => {
  // Debug log removed
  
  const [quoteData, setQuoteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState({
    quality: '',
    adherence: '',
    delivery: '',
    price: ''
  });
  const [isEvaluating, setIsEvaluating] = useState(false);

  useEffect(() => {
    // Debug log removed
    if (isOpen && quote) {
      // Use the passed quote data directly instead of making API calls

      setQuoteData(quote);
      
      // Parse evaluation if it exists
      if (quote.quotation_evaluation && quote.quotation_evaluation !== "") {
        try {
          // Handle both JSON and serialized PHP data
          let evalData;
          
          // Try to parse as JSON first
          if (quote.quotation_evaluation.startsWith('[') || quote.quotation_evaluation.startsWith('{')) {
            evalData = JSON.parse(quote.quotation_evaluation);
          } else {
            // If it's not JSON, it might be serialized PHP data - handle as array
            console.warn('Non-JSON evaluation data detected:', quote.quotation_evaluation);
            // For now, set default values
            evalData = ['', '', '', ''];
          }
          
          if (Array.isArray(evalData)) {
            setEvaluation({
              quality: evalData[0] || '',
              adherence: evalData[1] || '',
              delivery: evalData[2] || '',
              price: evalData[3] || ''
            });
          } else if (typeof evalData === 'object') {
            setEvaluation({
              quality: evalData.quality || '',
              adherence: evalData.adherence || '',
              delivery: evalData.delivery || '',
              price: evalData.price || ''
            });
          }
        } catch (e) {
          console.error('Error parsing evaluation data:', e, 'Data:', quote.quotation_evaluation);
          // Reset to default values on error
          setEvaluation({
            quality: '',
            adherence: '',
            delivery: '',
            price: ''
          });
        }
      } else {
        // Reset evaluation if no data
        setEvaluation({
          quality: '',
          adherence: '',
          delivery: '',
          price: ''
        });
      }
    } else if (isOpen && quoteId && rfqId) {
      // Fallback to API call if no quote prop is provided (for backward compatibility)
      fetchQuoteData();
    }
  }, [isOpen, quote, quoteId, rfqId]);

  const fetchQuoteData = async () => {
    setLoading(true);
    try {
      // Debug log removed
      const response = await getQuotationEvaluation(quoteId);
      
      if (response.status === 1) {
        const data = response.data;
        setQuoteData(data);
        
        // Parse evaluation if it exists
        if (data.quotation_evaluation && data.quotation_evaluation !== "") {
          try {
            // Handle both JSON and serialized PHP data
            let evalData;
            
            // Try to parse as JSON first
            if (data.quotation_evaluation.startsWith('[') || data.quotation_evaluation.startsWith('{')) {
              evalData = JSON.parse(data.quotation_evaluation);
            } else {
              // If it's not JSON, it might be serialized PHP data - handle as array
              console.warn('Non-JSON evaluation data detected:', data.quotation_evaluation);
              // For now, set default values
              evalData = ['', '', '', ''];
            }
            
            if (Array.isArray(evalData)) {
              setEvaluation({
                quality: evalData[0] || '',
                adherence: evalData[1] || '',
                delivery: evalData[2] || '',
                price: evalData[3] || ''
              });
            } else if (typeof evalData === 'object') {
              setEvaluation({
                quality: evalData.quality || '',
                adherence: evalData.adherence || '',
                delivery: evalData.delivery || '',
                price: evalData.price || ''
              });
            }
          } catch (e) {
            console.error('Error parsing evaluation data:', e, 'Data:', data.quotation_evaluation);
            // Reset to default values on error
            setEvaluation({
              quality: '',
              adherence: '',
              delivery: '',
              price: ''
            });
          }
        } else {
          // Reset evaluation if no data
          setEvaluation({
            quality: '',
            adherence: '',
            delivery: '',
            price: ''
          });
        }
      } else {
        throw new Error(response.message || 'Failed to fetch quote data');
      }
    } catch (error) {
      console.error('Error fetching quote data:', error);
      // Set some fallback data so the modal still shows
      setQuoteData({
        quotation_company_name: 'Error loading data',
        quotation_email_address: '',
        quotation_tel: '',
        quotation_physical_address: '',
        procurement_items: '[]',
        procurement_qty: '[]',
        quotation_comments: '[]',
        quotation_amounts: '[]',
        quotation_units: '[]',
        quotation_vats: '',
        quotation_vat: 0,
        quotation_total: 0,
        files: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluationChange = (field, value) => {
    // Allow empty string for temporary states while typing
    if (value === '') {
      setEvaluation(prev => ({
        ...prev,
        [field]: ''
      }));
      return;
    }
    
    const numValue = parseInt(value);
    // Only update if it's a valid number between 1-4
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 4) {
      setEvaluation(prev => ({
        ...prev,
        [field]: numValue
      }));
    }
    // If invalid, don't update state but don't return early either
  };

  const handleSaveEvaluation = async () => {
    const values = [evaluation.quality, evaluation.adherence, evaluation.delivery, evaluation.price];
    if (values.some(v => !v || v < 1 || v > 4)) {
      toast.error('Please provide all evaluation scores (1-4)');
      return;
    }
    
    try {
      // If we have an onEvaluationSave callback, use it instead of making direct API calls
      if (onEvaluationSave) {
        const callbackQuoteId = quoteData?.quotation_id || quoteId;

        const success = await onEvaluationSave(callbackQuoteId, evaluation);
        if (success) {
          // Don't show success message here, the callback will show it
          setIsEvaluating(false);
          
          // Update the quote data with the new evaluation
          if (quoteData) {
            const updatedQuoteData = {
              ...quoteData,
              quotation_evaluation: JSON.stringify(values)
            };
            setQuoteData(updatedQuoteData);
          }
          
          // Close the modal after successful save
          onClose();
        } else {
          // Only show error message if the callback didn't handle it
          toast.error('Failed to save evaluation');
        }
        return;
      }

      // Fallback to quotation model for backward compatibility
      const evaluationQuoteId = quoteData?.quotation_id || quoteId;

      const response = await saveEvaluation(evaluationQuoteId, evaluation);
      
      if (response.status === 1) {
        toast.success('Evaluation saved successfully');
        setIsEvaluating(false);
        
        // Update the quote data with the new evaluation
        if (quoteData) {
          const updatedQuoteData = {
            ...quoteData,
            quotation_evaluation: JSON.stringify(values)
          };
          setQuoteData(updatedQuoteData);
        }
        
        // Close the modal after successful save
        onClose();
      } else {
        throw new Error(response.message || 'Failed to save evaluation');
      }
    } catch (error) {
      console.error('Error saving evaluation:', error);
      toast.error('Failed to save evaluation');
    }
  };

  const handleQuoteEvaluate = (action) => {
    if (action === 'toggle') {
      setIsEvaluating(!isEvaluating);
    }
  };

  const getEvaluationBadge = (total) => {
    if (total >= 13) return { text: 'Excellent', class: 'bg-green-100 text-green-800' };
    if (total >= 9) return { text: 'Above Average', class: 'bg-blue-100 text-blue-800' };
    if (total >= 5) return { text: 'Average', class: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Poor', class: 'bg-red-100 text-red-800' };
  };

  // Handle data that can be arrays or serialized strings
  const safeDataParse = (data, fallback = []) => {
    if (!data || data === null || data === 'null') return fallback;
    
    // If it's already an array, return it
    if (Array.isArray(data)) return data;
    
    const dataStr = typeof data === 'string' ? data.trim() : String(data);
    if (dataStr === '' || dataStr === 'null') return fallback;
    
    try {
      // Try to parse as JSON
      if (dataStr.startsWith('[') || dataStr.startsWith('{')) {
        return JSON.parse(dataStr);
      }
      
      // If it starts with 'a:' it's PHP serialized data - we can't parse it directly in JS
      if (dataStr.startsWith('a:') || dataStr.startsWith('s:')) {
        console.warn('PHP serialized data detected, using fallback:', dataStr.substring(0, 50) + '...');
        return fallback;
      }
      
      return fallback;
    } catch (e) {
      console.warn('Failed to parse data:', dataStr.substring(0, 50) + '...', e);
      return fallback;
    }
  };

  // Parse VAT data - handles both arrays and PHP serialized strings
  const parseVatData = (data) => {
    // First try to use safeDataParse for arrays and JSON
    const parsedArray = safeDataParse(data, []);
    if (parsedArray.length > 0) {
      // Convert string values to numbers
      return parsedArray.map(val => parseFloat(val) || 0);
    }
    
    // Fallback to PHP serialized parsing if it's a string
    if (!data || typeof data !== 'string' || data === 'null') return [];
    
    // This is a simple parser for PHP array serialization like: a:2:{i:0;d:175.5;i:1;i:210;}
    try {
      const matches = data.match(/a:(\d+):\{(.+)\}/);
      if (!matches) return [];
      
      const content = matches[2];
      const values = [];
      
      // Extract values using regex - handles both integers and doubles
      const valueMatches = content.match(/(i:\d+;(?:d:[\d.]+|i:\d+);)/g);
      if (valueMatches) {
        valueMatches.forEach(match => {
          // Extract the value part (after the second semicolon)
          const valueMatch = match.match(/;(?:d:([\d.]+)|i:(\d+));/);
          if (valueMatch) {
            const value = valueMatch[1] || valueMatch[2];
            values.push(parseFloat(value));
          }
        });
      }
      
      return values;
    } catch (e) {
      console.error('Failed to parse VAT data:', e);
      return [];
    }
  };

  // Helper function to parse financial values
  const parseFinancialValue = (value) => {
    if (!value || value === null || value === 'null') return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  if (!isOpen) {
    // Debug log removed
    return null;
  }

  if (!quoteData) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
          </div>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
            <div className="bg-white px-6 pt-5 pb-4">
              {loading ? (
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  <p className="mt-2">Loading...</p>
                </div>
              ) : (
                <p>Failed to load quote data</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalScore = Object.values(evaluation).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
  const badge = getEvaluationBadge(totalScore);
  
  // Check if quote has saved evaluation data - only check the persisted data, not current input state
  const hasEvaluation = quoteData.quotation_evaluation && 
                        quoteData.quotation_evaluation !== '' && 
                        quoteData.quotation_evaluation !== 'null';

  const items = safeDataParse(quoteData.procurement_items, []);
  const quantities = safeDataParse(quoteData.procurement_qty, []);
  const comments = safeDataParse(quoteData.quotation_comments, []);
  const units = safeDataParse(quoteData.quotation_units, []);
  const lineVats = parseVatData(quoteData.quotation_vats);

  // If quote doesn't have items, try to get them from the specification
  // This happens when the quote references the original specification items
  const actualItems = items.length > 0 ? items : 
    (quoteData.specification_items || []);
  const actualQuantities = quantities.length > 0 ? quantities : 
    (quoteData.specification_qty || []);

  // Calculate totals correctly based on the data structure
  const calculatedTotals = actualItems.reduce((totals, _, index) => {
    const unitPriceExVat = parseFinancialValue(units[index]); // units already contains price EXCLUDING VAT
    const qty = parseFloat(actualQuantities[index] || 0);
    const lineVat = lineVats[index] || 0;
    
    // Calculate line totals
    const lineNetTotal = unitPriceExVat * qty; // Total excluding VAT
    const lineTotal = lineNetTotal + lineVat; // Total including VAT
    
    return {
      netTotal: totals.netTotal + lineNetTotal,
      vatTotal: totals.vatTotal + lineVat,
      grandTotal: totals.grandTotal + lineTotal
    };
  }, { netTotal: 0, vatTotal: 0, grandTotal: 0 });

  // Use calculated totals, fallback to database values if calculation fails
  const displayNetTotal = calculatedTotals.netTotal > 0 ? calculatedTotals.netTotal : 
    (parseFinancialValue(quoteData.quotation_total) - parseFinancialValue(quoteData.quotation_vat));
  const displayVatTotal = calculatedTotals.vatTotal > 0 ? calculatedTotals.vatTotal : 
    parseFinancialValue(quoteData.quotation_vat);
  const displayGrandTotal = calculatedTotals.grandTotal > 0 ? calculatedTotals.grandTotal : 
    parseFinancialValue(quoteData.quotation_total);

  // Debug log removed

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 pt-5 pb-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Quote from {quoteData.quotation_company_name}
                </h3>
                {canMakeCorrections && !hasEvaluation && (
                  <button
                    onClick={() => handleQuoteEvaluate('toggle')}
                    className="ml-4 inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Evaluate
                  </button>
                )}
              </div>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Compact Supplier Details Row */}
            <div className="mt-4 bg-gray-50 rounded-lg p-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Contact:</span>
                  <p className="text-gray-900 truncate">{quoteData.quotation_tel}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Email:</span>
                  <p className="text-gray-900 truncate">{quoteData.quotation_email_address}</p>
                </div>
                <div className="md:col-span-2">
                  <span className="font-medium text-gray-600">Address:</span>
                  <p className="text-gray-900 truncate">{quoteData.quotation_physical_address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Quote Items - Takes up 2/3 of the width */}
              <div className="md:col-span-2">
                <h4 className="text-md font-medium text-gray-900 mb-4">Quotation Items</h4>
                
                {/* Responsive table that stacks on mobile */}
                <div className="space-y-3 lg:space-y-0">
                  {/* Desktop Table View */}
                  <div className="hidden lg:block">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Qty</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Unit Price</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">VAT</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {actualItems.map((item, index) => {
                          const unitPriceExVat = parseFinancialValue(units[index]); // units already contains price EXCLUDING VAT
                          const qty = parseFloat(actualQuantities[index] || 0);
                          const lineVat = lineVats[index] || 0;
                          
                          // Calculate line totals
                          const lineNetTotal = unitPriceExVat * qty; // Total excluding VAT
                          const lineTotal = lineNetTotal + lineVat; // Total including VAT
                          
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{item}</div>
                                  {comments[index] && (
                                    <div className="text-gray-500 text-xs mt-1">{comments[index]}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-center">{qty}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                                R{unitPriceExVat.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                                R{lineVat.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                                R{lineTotal.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-3">
                    {actualItems.map((item, index) => {
                      const unitPriceExVat = parseFinancialValue(units[index]); // units already contains price EXCLUDING VAT
                      const qty = parseFloat(actualQuantities[index] || 0);
                      const lineVat = lineVats[index] || 0;
                      
                      // Calculate line totals
                      const lineNetTotal = unitPriceExVat * qty; // Total excluding VAT
                      const lineTotal = lineNetTotal + lineVat; // Total including VAT
                      
                      return (
                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                          <div className="font-medium text-gray-900 mb-2">{item}</div>
                          {comments[index] && (
                            <div className="text-gray-500 text-xs mb-2">{comments[index]}</div>
                          )}
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600">Qty:</span> {qty}
                            </div>
                            <div>
                              <span className="text-gray-600">Unit:</span> R{unitPriceExVat.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                            </div>
                            <div>
                              <span className="text-gray-600">VAT:</span> R{lineVat.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                            </div>
                            <div>
                              <span className="text-gray-600 font-medium">Total:</span> <span className="font-medium">R{lineTotal.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Totals */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-end">
                    <div className="w-64">
                      <div className="flex justify-between py-1">
                        <span className="text-sm text-gray-600">Net Total:</span>
                        <span className="text-sm font-medium">R{displayNetTotal.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-sm text-gray-600">VAT:</span>
                        <span className="text-sm font-medium">R{displayVatTotal.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between py-2 border-t border-gray-200">
                        <span className="text-base font-medium text-gray-900">Grand Total:</span>
                        <span className="text-base font-bold text-gray-900">R{displayGrandTotal.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>
                      </div>
                     
                    </div>
                  </div>
                </div>
              </div>

              {/* Evaluation Panel - Takes up 1/4 of the width */}
              <div className="lg:col-span-1">
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  Evaluation 
                  {hasEvaluation && (
                    <span className={`ml-2 block mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>
                      {totalScore}/16 - {badge.text}
                    </span>
                  )}
                </h4>

                {isEvaluating && !hasEvaluation ? (
                  <div className="space-y-3">
                    {/* Compact Rating Scale */}
                    <div className="bg-blue-50 p-2 rounded-lg">
                      <div className="text-xs text-blue-600 mb-1">Rating Scale:</div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <span className="bg-red-100 text-red-800 px-1 py-0.5 rounded text-center">1-Poor</span>
                        <span className="bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded text-center">2-Avg</span>
                        <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-center">3-Good</span>
                        <span className="bg-green-100 text-green-800 px-1 py-0.5 rounded text-center">4-Excel</span>
                      </div>
                    </div>

                    {/* Compact 2x2 Grid for Evaluation Inputs */}
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'quality', label: 'Quality' },
                        { key: 'adherence', label: 'Adherence' },
                        { key: 'delivery', label: 'Delivery' },
                        { key: 'price', label: 'Price' }
                      ].map(({ key, label }) => (
                        <div key={key} className="space-y-1">
                          <label className="block text-xs font-medium text-gray-700">{label}</label>
                          <input
                            type="number"
                            min="1"
                            max="4"
                            value={evaluation[key]}
                            onChange={(e) => handleEvaluationChange(key, e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-center"
                            placeholder="1-4"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Compact Save Button */}
                    <button
                      onClick={handleSaveEvaluation}
                      disabled={Object.values(evaluation).some(v => !v)}
                      className="w-full bg-green-600 text-white py-1.5 px-3 text-sm rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save Evaluation
                    </button>
                  </div>
                ) : hasEvaluation ? (
                  <div className="space-y-2">
                    {/* Compact Evaluation Display */}
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {[
                          { key: 'quality', label: 'Quality', value: evaluation.quality },
                          { key: 'adherence', label: 'Adherence', value: evaluation.adherence },
                          { key: 'delivery', label: 'Delivery', value: evaluation.delivery },
                          { key: 'price', label: 'Price', value: evaluation.price }
                        ].map(({ label, value }) => (
                          <div key={label} className="flex justify-between py-0.5">
                            <span className="text-gray-600">{label}:</span>
                            <span className="font-medium">{value}/4</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-xs">No evaluation yet</div>
                )}

                {/* Files - Show both attachments and legacy files */}
                {((quoteData.attachments && quoteData.attachments.length > 0) || (quoteData.files && quoteData.files.length > 0)) && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Attachments ({(quoteData.attachments?.length || 0) + (quoteData.files?.length || 0)})
                    </h4>
                    <div className="space-y-2">
                      {/* First show new standardized attachments */}
                      {quoteData.attachments && quoteData.attachments.length > 0 && quoteData.attachments.map((attachment, index) => {
                        const fileName = attachment.file_name || `File ${index + 1}`;
                        const filePath = attachment.file_path;
                        const fileUrl = `${API_BASE_URL}/api/attachments/quotations/${filePath}`;
                        
                        return (
                          <div
                            key={`attachment-${index}-${attachment.attachment_id}`}
                            className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors group"
                          >
                            <div className="flex-1 min-w-0 mr-3">
                              <p className="text-xs font-medium text-gray-900 truncate" title={fileName}>
                                {fileName}
                              </p>
                            </div>
                            <div className="flex space-x-2 flex-shrink-0">
                              <button
                                onClick={() => window.open(fileUrl + '?v=' + Date.now(), '_blank')}
                                className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = fileUrl + '?download=1';
                                  link.download = fileName;
                                  link.click();
                                }}
                                className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Then show legacy files with old download method */}
                      {quoteData.files && quoteData.files.length > 0 && quoteData.files.map((file, index) => {
                        const fileName = file.quotation_file_name || file.quotation_file || `File ${index + 1}`;
                        // Construct proper file path: {quotation_directory}/{filename}
                        // Note: FILES constant already includes directory/project/ so we don't need to prepend 'directory/'
                        const filePath = quoteData.quotation_directory ? 
                          `${quoteData.quotation_directory}/${file.quotation_file}` : 
                          file.quotation_file;
                        
                        return (
                          <div
                            key={`file-${index}-${file.quotation_file_id || index}`}
                            className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors group"
                          >
                            <div className="flex-1 min-w-0 mr-3">
                              <p className="text-xs font-medium text-gray-900 truncate" title={fileName}>
                                {fileName} <span className="text-xs text-gray-400">(legacy)</span>
                              </p>
                            </div>
                            <div className="flex space-x-2 flex-shrink-0">
                              <button
                                onClick={() => {
                                  // Use backend controller for forced download
                                  const downloadUrl = `${API_BASE_URL}/files/storage?file=${encodeURIComponent(filePath)}`;
                                  window.open(downloadUrl, '_blank');
                                }}
                                className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                                title="Download (Legacy)"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Make Corrections Link */}
                {/* {canMakeCorrections && (
                  <div className="mt-4">
                    <a 
                      href={`/requisitions/amend/${specificationId}?requisition=${requisitionId}`}
                      className="block w-full text-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    >
                      Make Corrections
                    </a>
                  </div>
                )} */}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 sm:flex sm:flex-row-reverse">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteViewModal;
